import type { TempoEvent, TempoRampShape } from '@/domain/types';
import type { MidiTempoEvent } from '@/util/midiFile';
import { normaliseTempoMap, tempoAt } from '@/timing/tempoMap';

/**
 * Tempo ramps and Standard MIDI Files.
 *
 * MIDI has no ramp. A file holds a list of tempos, each in force until the
 * next, so a ritardando written here as one event — start here, end there,
 * this shape — has to leave as a *run* of events dense enough that the steps
 * between them are inaudible, and a file that arrives carrying such a run is
 * better read as the ramp it was than as forty tempo changes the user would
 * have to delete one by one to edit.
 *
 * ## Writing
 *
 * A ramping segment is cut into steps of an eighth of a beat. Each step is
 * given the ramp's tempo at its *midpoint*, which does two things: the steps
 * of a straight ramp are then exactly evenly spaced in BPM and those of an even
 * ramp exactly evenly spaced in ratio, so the run can be recognised on the way
 * back in without guesswork; and the timing error of a stepped ramp against
 * the continuous one is second order in the step — a few hundred microseconds
 * over an eight-beat ritardando, which no listener and no test that measures
 * onsets to a hundredth of a second can tell. The segment's own end event
 * follows the run and carries the tempo the ramp arrives at.
 *
 * ## Reading
 *
 * A run is at least four tempo events at one constant spacing of half a beat
 * or less, moving monotonically, followed by the event they arrive at. Its
 * steps are tested against both shapes — the same difference every step, or
 * the same ratio — and the one that fits within a quarter of a percent is the
 * ramp; if neither does, the run is left as the changes it is, because a fit
 * that is not there is not to be invented. The ramp starts half a step before
 * the first step's tempo, which is where a midpoint-sampled run puts its
 * start, so a run written here reads back as exactly the ramp that wrote it.
 * A run from another sequencer, sampled at each step's start rather than its
 * middle, reads back as a ramp through its steps to within half a step.
 */

/** Steps per beat when a ramp is written as a run. */
export const RAMP_STEPS_PER_BEAT = 8;
/** The fewest steps that read back as a ramp rather than as changes. */
const MIN_RUN_STEPS = 3;
/** How far a step may sit from the shape it is tested against, relative. */
const FIT_TOLERANCE = 0.0025;

/** The tempo map as a MIDI file holds it: every ramp written as a run of steps. */
export function flattenTempoRamps(events: readonly TempoEvent[], ppq: number): MidiTempoEvent[] {
  const map = normaliseTempoMap(events, ppq);
  const out: MidiTempoEvent[] = [];
  const stepTicks = Math.max(1, Math.round(ppq / RAMP_STEPS_PER_BEAT));
  for (let i = 0; i < map.events.length; i++) {
    const event = map.events[i];
    const next = map.events[i + 1];
    const ramps = event.ramp === true && next !== undefined && Math.abs(next.bpm - event.bpm) > 1e-9;
    if (!ramps) {
      out.push({ tick: event.tick, bpm: event.bpm });
      continue;
    }
    // Equal steps, as near as whole ticks allow: a segment that is not a
    // whole number of eighths gets steps a tick apart in width rather than a
    // short one at the end, so the run reads back as one spacing.
    const length = next.tick - event.tick;
    const steps = Math.max(1, Math.round(length / stepTicks));
    for (let k = 0; k < steps; k++) {
      const tick = event.tick + Math.round((k * length) / steps);
      const end = event.tick + Math.round(((k + 1) * length) / steps);
      out.push({ tick, bpm: tempoAt(map, (tick + end) / 2) });
    }
  }
  return out;
}

/** A tempo list with every run of steps folded back into the ramp it describes. */
export function foldTempoRuns(events: readonly MidiTempoEvent[], ppq: number): TempoEvent[] {
  const sorted = [...events].sort((a, b) => a.tick - b.tick);
  const out: TempoEvent[] = [];
  let i = 0;
  while (i < sorted.length) {
    const run = runStartingAt(sorted, i, ppq);
    if (run) {
      out.push({ tick: sorted[i].tick, bpm: run.startBpm, ramp: true, ...(run.shape === 'exponential' ? { rampShape: run.shape } : {}) });
      i += run.steps;
      continue;
    }
    out.push({ tick: sorted[i].tick, bpm: sorted[i].bpm });
    i += 1;
  }
  return out;
}

interface Run {
  steps: number;
  startBpm: number;
  shape: TempoRampShape;
}

/** The longest run of evenly spaced, monotonic steps starting at `from`, if it is a ramp. */
function runStartingAt(events: readonly MidiTempoEvent[], from: number, ppq: number): Run | null {
  if (from + 1 >= events.length) return null;
  const spacing = events[from + 1].tick - events[from].tick;
  if (spacing <= 0 || spacing > ppq / 2) return null;
  const direction = Math.sign(events[from + 1].bpm - events[from].bpm);
  if (direction === 0) return null;

  // Extend while the spacing holds — to a tick, since equal steps cut from a
  // segment that is not a whole number of them differ by one — and the tempo
  // keeps moving the same way.
  let last = from + 1;
  while (
    last + 1 < events.length &&
    Math.abs(events[last + 1].tick - events[last].tick - spacing) <= 1 &&
    Math.sign(events[last + 1].bpm - events[last].bpm) === direction
  ) {
    last += 1;
  }
  // The run's final event is the one the steps arrive at, not a step itself.
  const steps = last - from;
  if (steps < MIN_RUN_STEPS) return null;
  const target = events[last].bpm;
  const first = events[from].bpm;
  const second = events[from + 1].bpm;

  // Straight: the same difference every step, so the start is half a step
  // before the first sample. The event the run arrives at sits half a step
  // past the last sample when the run was sampled at midpoints, and a whole
  // step past when it was sampled at step starts; either is the ramp's end.
  const difference = second - first;
  const linearStart = first - difference / 2;
  const linearFits =
    arrives(target, linearStart + difference * steps, linearStart + difference * (steps + 0.5)) &&
    stepsFit(events, from, steps, (k) => linearStart + difference * (k + 0.5));

  // Even: the same ratio every step.
  const ratio = second / first;
  const evenStart = first / Math.sqrt(ratio);
  const evenFits =
    arrives(target, evenStart * Math.pow(ratio, steps), evenStart * Math.pow(ratio, steps + 0.5)) &&
    stepsFit(events, from, steps, (k) => evenStart * Math.pow(ratio, k + 0.5));

  if (linearFits && !evenFits) return { steps, startBpm: linearStart, shape: 'linear' };
  if (evenFits && !linearFits) return { steps, startBpm: evenStart, shape: 'exponential' };
  if (linearFits && evenFits) {
    // Both fit a gentle ramp; take whichever predicts the steps more closely.
    const linearError = worstError(events, from, steps, (k) => linearStart + difference * (k + 0.5));
    const evenError = worstError(events, from, steps, (k) => evenStart * Math.pow(ratio, k + 0.5));
    return evenError < linearError
      ? { steps, startBpm: evenStart, shape: 'exponential' }
      : { steps, startBpm: linearStart, shape: 'linear' };
  }
  return null;
}

/** Whether the run's final event lies between the two places a ramp's end can be, give or take the tolerance. */
function arrives(actual: number, atMidpoints: number, atStarts: number): boolean {
  const low = Math.min(atMidpoints, atStarts) - FIT_TOLERANCE * Math.abs(actual);
  const high = Math.max(atMidpoints, atStarts) + FIT_TOLERANCE * Math.abs(actual);
  return actual >= low && actual <= high;
}

function stepsFit(events: readonly MidiTempoEvent[], from: number, steps: number, predict: (k: number) => number): boolean {
  return worstError(events, from, steps, predict) <= FIT_TOLERANCE;
}

function worstError(events: readonly MidiTempoEvent[], from: number, steps: number, predict: (k: number) => number): number {
  let worst = 0;
  for (let k = 0; k < steps; k++) {
    const actual = events[from + k].bpm;
    worst = Math.max(worst, Math.abs(actual - predict(k)) / Math.abs(actual));
  }
  return worst;
}
